import { createElement } from 'react'
import {
    Button,
    InputBase,
    Select,
    Text,
    Title,
    createTheme,
    type InputProps,
    type MantineColorsTuple,
    type MantineTheme,
} from '@mantine/core'
import { IconCaretDownFilled } from '@tabler/icons-react'
import rebrand from '@nile/mantine-theme/theme/rebrand'
import { lineHeightForSize } from '@/tenants/walmart/theme/walmartTypography'

export const WALMART_INK: MantineColorsTuple = [
    '#f1f1f2',
    '#e3e4e5',
    '#babbbe',
    '#94969b',
    '#74767c',
    '#3a3b3e',
    '#070821',
    '#05061a',
    '#040513',
    '#02030b',
]

// Shared General Medicine screens ask for their action color as GM blue or the
// highlight token; Walmart's actions are ink, and its blue ramp is for accents.
const GM_ACTION_COLOR = /^(blue(\.\d)?|var\(--(gm-blue-\d|gm-background-bg-highlight|gm-foreground-fg-highlight|mantine-color-blue-\d)\b.*)$/

function actionColorVars(color: string | undefined, variant: string | undefined): Record<string, string> {
    if (!color || !GM_ACTION_COLOR.test(color)) return {}
    if (variant === undefined || variant === 'filled') {
        return {
            '--button-bg': 'var(--walmart-action-fill-strong)',
            '--button-hover': 'var(--walmart-action-fill-hover)',
            '--button-color': 'var(--walmart-action-text-on-strong)',
            '--button-bd': '1px solid transparent',
        }
    }
    if (variant === 'outline') {
        return {
            '--button-color': 'var(--gm-foreground-fg-primary)',
            '--button-bd': '1px solid var(--gm-foreground-fg-primary)',
            '--button-hover': 'var(--walmart-action-surface-hover)',
        }
    }
    if (variant === 'subtle' || variant === 'transparent' || variant === 'light') {
        return { '--button-color': 'var(--gm-foreground-fg-primary)' }
    }
    return {}
}

const rebrandButton = rebrand.components?.Button
const rebrandTextInput = rebrand.components?.TextInput
const rebrandSelect = rebrand.components?.Select
const rebrandAutocomplete = rebrand.components?.Autocomplete
const rebrandText = rebrand.components?.Text
const rebrandTitle = rebrand.components?.Title

export const walmartTheme = createTheme({
    ...rebrand,
    primaryColor: 'walmartInk',
    colors: {
        ...rebrand.colors,
        walmartInk: WALMART_INK,
        blue: WALMART_INK,
    },
    components: {
        ...rebrand.components,
        // General Medicine route layouts nest a `rebrand` MantineProvider, which re-applies rebrand's
        // defaultProps over this theme, so the radii map rebrand's default radius in `vars` instead.
        Button: Button.extend({
            ...rebrandButton,
            vars: (_theme, props) => ({
                root: {
                    ...(props.radius === rebrandButton?.defaultProps?.radius
                        ? { '--button-radius': 'var(--gm-action-radius)' }
                        : {}),
                    ...actionColorVars(props.color, props.variant),
                },
            }),
        }),
        TextInput: {
            ...rebrandTextInput,
            vars: (_theme: MantineTheme, props: InputProps) =>
                props.radius === rebrandTextInput?.defaultProps?.radius
                    ? { wrapper: { '--input-radius': 'var(--gm-field-radius)' } }
                    : { wrapper: {} },
        },
        Autocomplete: {
            ...rebrandAutocomplete,
            vars: (_theme: MantineTheme, props: InputProps) =>
                props.radius === rebrandAutocomplete?.defaultProps?.radius
                    ? { wrapper: { '--input-radius': 'var(--gm-field-radius)' } }
                    : { wrapper: {} },
        },
        InputBase: InputBase.extend({
            defaultProps: { radius: 'var(--gm-field-radius)' },
            classNames: rebrand.components?.TextInput?.classNames,
        }),
        Text: Text.extend({
            ...rebrandText,
            vars: (_theme, props) => {
                const lh = props.inherit ? undefined : lineHeightForSize(props.fz)
                return lh ? { root: { '--text-lh': lh } } : { root: {} }
            },
        }),
        Title: Title.extend({
            ...rebrandTitle,
            vars: (_theme, props) => {
                const lh = lineHeightForSize(props.fz)
                return lh ? { root: { '--title-lh': lh } } : { root: {} }
            },
        }),
        Select: Select.extend({
            ...rebrandSelect,
            defaultProps: {
                ...rebrandSelect?.defaultProps,
                rightSection: createElement(IconCaretDownFilled, { size: 16, 'aria-hidden': true }),
            },
        }),
    },
})

export default walmartTheme
